import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  paidMediaOverviewService,
  type PaidMediaOverviewCampaignBenchmarks,
  type PaidMediaOverviewCampaignRow,
  type PaidMediaOverviewCampaignsParams,
  type PaidMediaOverviewPlatformFilter,
  type PaidMediaOverviewSortOrder,
  type PaidMediaOverviewWarning,
} from "src/services/PaidMediaOverviewAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  activeOverviewPlatforms,
  buildMockCampaignUniverse,
  composeCampaignRows,
  composeCampaignsPage,
  isMockOnlyPlatform,
  OVERVIEW_CURRENCY,
  type OverviewCompositionContext,
} from "../utils/paidMediaOverviewComposition";
import { useAsyncResource } from "./useAsyncResource";

/**
 * The live endpoint is queried unpaginated (one large page) because the table
 * merges its rows with the mocked platforms' campaigns before sorting and
 * paging — paging on the server would slice the wrong set.
 */
const MERGE_PAGE_SIZE = 100;

export interface UsePaidMediaOverviewCampaignsParams {
  startDate: string;
  endDate: string;
  granularity: OverviewCompositionContext["granularity"];
  platform: PaidMediaOverviewPlatformFilter;
  attributionMode: OverviewCompositionContext["attributionMode"];
  campaignId: string;
  objectiveOrNetwork: string;
  status: string;
  search: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: PaidMediaOverviewSortOrder;
}

export interface PaidMediaOverviewCampaignsBundle {
  rows: PaidMediaOverviewCampaignRow[];
  benchmarks: PaidMediaOverviewCampaignBenchmarks | null;
  currency: string | null;
  page: number;
  totalPages: number;
  totalRows: number;
  warnings: PaidMediaOverviewWarning[];
  loading: boolean;
  error: string | null;
  exporting: boolean;
  fetchAllRows: () => Promise<PaidMediaOverviewCampaignRow[]>;
}

export function usePaidMediaOverviewCampaigns({
  startDate,
  endDate,
  granularity,
  platform,
  attributionMode,
  campaignId,
  objectiveOrNetwork,
  status,
  search,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: UsePaidMediaOverviewCampaignsParams): PaidMediaOverviewCampaignsBundle {
  const { t, i18n } = useTranslation();
  const fallbackError = t("paidMediaOverview.errors.requestFailed");
  const [exporting, setExporting] = useState(false);
  const lang = getApiLangFromI18n(i18n);

  const baseParams = useMemo<PaidMediaOverviewCampaignsParams>(
    () => ({
      start_date: startDate,
      end_date: endDate,
      platform,
      campaign_id: campaignId || undefined,
      objective_or_network: objectiveOrNetwork || undefined,
      status: status || undefined,
      search: search.trim() || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      lang,
    }),
    [
      lang,
      startDate,
      endDate,
      platform,
      campaignId,
      objectiveOrNetwork,
      status,
      search,
      sortBy,
      sortOrder,
    ]
  );

  const request = useCallback(
    () =>
      paidMediaOverviewService.fetchCampaigns({
        ...baseParams,
        page: 1,
        page_size: MERGE_PAGE_SIZE,
      }),
    [baseParams]
  );

  const apiEnabled = Boolean(startDate && endDate) && !isMockOnlyPlatform(platform);
  const campaigns = useAsyncResource(apiEnabled ? request : null, fallbackError);

  const context = useMemo<OverviewCompositionContext>(
    () => ({
      startDate,
      endDate,
      granularity,
      platformFilter: platform,
      attributionMode,
      campaignId,
      objectiveOrNetwork,
      status,
    }),
    [
      startDate,
      endDate,
      granularity,
      platform,
      attributionMode,
      campaignId,
      objectiveOrNetwork,
      status,
    ]
  );

  const universe = useMemo(
    () => buildMockCampaignUniverse(context, activeOverviewPlatforms(context.platformFilter)),
    [context]
  );

  const mergedRows = useMemo(
    () => composeCampaignRows(context, universe, campaigns.data?.data?.rows ?? [], search),
    [context, universe, campaigns.data, search]
  );

  const pageData = useMemo(
    () => composeCampaignsPage(mergedRows, { sortBy, sortOrder, page, pageSize }),
    [mergedRows, sortBy, sortOrder, page, pageSize]
  );

  const fetchAllRows = useCallback(async () => {
    setExporting(true);
    try {
      return composeCampaignsPage(mergedRows, {
        sortBy,
        sortOrder,
        page: 1,
        pageSize: Math.max(1, mergedRows.length),
      }).rows;
    } finally {
      setExporting(false);
    }
  }, [mergedRows, sortBy, sortOrder]);

  return {
    rows: pageData.rows,
    benchmarks: pageData.benchmarks,
    currency: OVERVIEW_CURRENCY,
    page: pageData.page,
    totalPages: pageData.total_pages,
    totalRows: pageData.total_rows,
    warnings: campaigns.data?.meta?.warnings ?? [],
    loading: campaigns.loading,
    error: campaigns.error,
    exporting,
    fetchAllRows,
  };
}
